import { useAccount, useConnect, useDisconnect, useChainId, useSwitchChain } from 'wagmi';
import { useCallback, useMemo, useEffect, useState } from 'react';
import { bsc } from 'wagmi/chains';
import { xphereMainnet } from '../config/wagmi';

export const useWagmiWallet = () => {
  const { address, isConnected, isConnecting, isDisconnected } = useAccount();
  const { connect, connectors, error: connectError, isPending: isConnectPending } = useConnect();
  const { disconnect } = useDisconnect();
  const chainId = useChainId();
  const { switchChain, isPending: isSwitchPending } = useSwitchChain();
  
  // Watch for account changes (wallet switching, reconnection, etc.)
  // Note: We'll use the address from useAccount directly
  
  // State for auto-connection
  const [isAutoConnecting, setIsAutoConnecting] = useState(false);
  const [lastConnectedAccount, setLastConnectedAccount] = useState(null);

  // Debug connectors on mount
  useEffect(() => {
    console.log('🔌 Wagmi connectors loaded:', connectors.map(c => ({ id: c.id, name: c.name, type: c.type })));
  }, [connectors]);

  // Mainnet-only supported chains
  const supportedChains = useMemo(() => {
    return {
      bscMainnet: {
        name: 'BSC Mainnet',
        chainId: bsc.id,
        rpcUrl: 'https://bsc-dataseed.binance.org',
        explorer: 'https://bscscan.com',
        nativeCurrency: {
          name: 'BNB',
          symbol: 'BNB',
          decimals: 18
        },
        color: '🟡'
      },
      xphereMainnet: {
        name: 'Xphere Mainnet',
        chainId: xphereMainnet.id,
        rpcUrl: 'https://xp-mainnet.rpc.xplorium.xyz',
        explorer: 'https://xploriumscan.com',
        nativeCurrency: {
          name: 'XPH',
          symbol: 'XPH',
          decimals: 18
        },
        color: '🔴'
      }
    };
  }, []);

  // Helper function to detect commonly installed wallets  
  const detectInstalledWallet = useCallback(() => {
    if (typeof window === 'undefined') return null;
    
    // Priority order: Rabby -> Trust Wallet -> MetaMask -> Coinbase -> Other
    if (window.rabby?.ethereum) return 'rabby';
    if (window.trustwallet?.ethereum) return 'trustWallet';
    if (window.ethereum?.isMetaMask && !window.ethereum.isRabby) return 'metaMask';
    if (window.coinbaseWalletExtension) return 'coinbaseWallet';
    if (window.ethereum && !window.ethereum.isMetaMask && !window.rabby?.ethereum && !window.trustwallet?.ethereum) return 'injected';
    return null;
  }, []);

  // Connect wallet
  const connectWallet = useCallback(async (connectorId = null) => {
    // If no specific connector requested, try to auto-detect installed wallets
    if (!connectorId) {
      const detectedWallet = detectInstalledWallet();
      if (detectedWallet && connectors.some(c => c.id === detectedWallet)) {
        connectorId = detectedWallet;
        console.log(`🎯 Auto-detected wallet: ${detectedWallet}`);
      } else {
        console.log(`❌ No installed wallets detected. Please install a wallet extension.`);
        throw new Error('No wallet extension detected. Please install a wallet like MetaMask, Rabby Wallet, or Trust Wallet.');
      }
    }
    
    console.log('Available connectors:', connectors.map(c => ({ id: c.id, name: c.name })));
    console.log('Looking for connector:', connectorId);
    
    // Clear manual disconnect flag when user connects
    localStorage.removeItem('zk-bridge-manually-disconnected');
    
    let connector = connectors.find(c => c.id === connectorId);
    
    // If connector not found, try alternatives
    if (!connector) {
      if (connectorId === 'walletConnect') {
        connector = connectors.find(c => c.id === 'walletConnect' || c.type === 'walletConnect');
      } else {
        // Try to find any injected connector with matching name
        connector = connectors.find(c => c.type === 'injected' && c.id === connectorId);
      }
    }
    
    if (!connector) {
      console.error('Available connectors:', connectors.map(c => c.id));
      throw new Error(`Connector ${connectorId} not found. Available: ${connectors.map(c => c.id).join(', ')}`);
    }

    try {
      await connect({ connector });
      return true;
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      throw error;
    }
  }, [connect, connectors, detectInstalledWallet]);

  // Switch to a specific network
  const switchToNetwork = useCallback(async (networkKey) => {
    const targetNetwork = supportedChains[networkKey];
    if (!targetNetwork) {
      throw new Error(`Network ${networkKey} not supported`);
    }

    try {
      await switchChain({ chainId: targetNetwork.chainId });
      return true;
    } catch (error) {
      console.error('Failed to switch network:', error);
      throw error;
    }
  }, [supportedChains, switchChain]);

  // Auto-connect to wallet if previously connected (only on initial load)
  useEffect(() => {
    const autoConnect = async () => {
      // Early returns to prevent loops
      if (isAutoConnecting || isConnected || isConnecting) return;
      
      // Check if user was previously connected
      const lastAccount = localStorage.getItem('zk-bridge-last-account');
      if (!lastAccount) return;
      
      // Check if user manually disconnected (don't auto-reconnect)
      const wasManuallyDisconnected = localStorage.getItem('zk-bridge-manually-disconnected');
      if (wasManuallyDisconnected === 'true') return;
      
      // Check if any wallet is available
      if (typeof window.ethereum === 'undefined') return;
      
      // Check if we already attempted auto-connection (prevent loops)
      const autoConnectionAttempted = localStorage.getItem('zk-bridge-auto-connection-attempted');
      if (autoConnectionAttempted === 'true') return;
      
      try {
        setIsAutoConnecting(true);
        localStorage.setItem('zk-bridge-auto-connection-attempted', 'true');
        console.log('🔄 Auto-connecting to previously connected wallet...');
        
        // Check if any wallets are available before trying to connect
        if (connectors.length === 0) {
          console.log('📋 No wallet extensions detected. Users can manually connect.');
          return; // Skip auto-connection
        }
        
        // Auto-detect user's wallet and connect
        await connectWallet();
        console.log('✅ Auto-connection successful');
      } catch (error) {
        console.log('⚠️ Auto-connection failed:', error.message);
        // Only clear stored account if connection was attempted but failed
        if (error.message.includes('No wallet extension detected')) {
          console.log('📋 No wallet extensions detected - skipping auto-connection');
        } else {
          localStorage.removeItem('zk-bridge-last-account');
        }
        localStorage.removeItem('zk-bridge-auto-connection-attempted'); // Clear flag
      } finally {
        setIsAutoConnecting(false);
      }
    };

    autoConnect();
    
    // Cleanup function to reset flag if component unmounts
    return () => {
      localStorage.removeItem('zk-bridge-auto-connection-attempted');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally runs only once on mount to prevent loops

  // Track account changes and network switches
  useEffect(() => {
    if (address && address !== lastConnectedAccount) {
      console.log('👤 Account changed:', { from: lastConnectedAccount, to: address });
      setLastConnectedAccount(address);
      
      // Store the connected account
      localStorage.setItem('zk-bridge-last-account', address);
    }
  }, [address, lastConnectedAccount]);


  // Auto-switch to supported network when wallet connects to unsupported network
  useEffect(() => {
    if (!isConnected || !chainId || isSwitchPending) return;
    
    const isSupported = Object.values(supportedChains).some(network => network.chainId === chainId);
    
    if (!isSupported) {
      console.log('⚠️ Connected to unsupported network, attempting to switch...');
      
      // Get the first supported network as default
      const defaultNetwork = Object.values(supportedChains)[0];
      if (defaultNetwork) {
        switchToNetwork(Object.keys(supportedChains)[0]).catch(error => {
          console.log('Failed to auto-switch network:', error.message);
        });
      }
    }
  }, [isConnected, chainId, isSwitchPending, supportedChains, switchToNetwork]);

  // Get current network info
  const currentNetwork = useMemo(() => {
    if (!chainId) return null;
    
    const networkKey = Object.keys(supportedChains).find(key => 
      supportedChains[key].chainId === chainId
    );
    
    return networkKey ? supportedChains[networkKey] : null;
  }, [chainId, supportedChains]);

  // Check if current network is supported
  const isCorrectNetwork = useMemo(() => {
    if (!chainId) return false;
    return Object.values(supportedChains).some(network => network.chainId === chainId);
  }, [chainId, supportedChains]);

  // Get network error message
  const networkError = useMemo(() => {
    if (!isConnected) return null;
    if (isCorrectNetwork) return null;
    
    const supportedChainNames = Object.values(supportedChains).map(n => n.name).join(' or ');
    return `Please switch to ${supportedChainNames}`;
  }, [isConnected, isCorrectNetwork, supportedChains]);


  // Enhanced disconnect function that tracks manual disconnection
  const disconnectWallet = useCallback(() => {
    // Mark that user manually disconnected
    localStorage.setItem('zk-bridge-manually-disconnected', 'true');
    // Clear the last account
    localStorage.removeItem('zk-bridge-last-account');
    // Clear auto-connection attempt flag
    localStorage.removeItem('zk-bridge-auto-connection-attempted');
    // Disconnect the wallet
    disconnect();
  }, [disconnect]);

  // Get available connectors
  const availableConnectors = useMemo(() => {
    return connectors.map(connector => ({
      id: connector.id,
      name: connector.name,
      icon: connector.icon,
    }));
  }, [connectors]);


  return {
    // Connection state
    isConnected,
    isConnecting,
    isDisconnected,
    account: address,
    
    // Network state
    currentNetwork,
    isCorrectNetwork,
    networkError,
    supportedChains,
    
    // Auto-connection state
    isAutoConnecting,
    
    // Actions
    connectWallet,
    disconnect: disconnectWallet,
    switchToNetwork,
    
    // Loading states
    isConnectPending,
    isSwitchPending,
    
    // Errors
    connectError,
    
    // Available connectors
    availableConnectors,
  };
};
